import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCareRequestService, useRegistrationIntakeService, useCareCaseService, useRegistrationService,
  useMemberService,
  type CareRequestModelView, type RegistrationIntakeModelView,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import type { Row } from '../types';
import { runPool } from '../Enrich/pool';
import {
  failedResult, missingOf, resolveRequest, type PersonResult, type RecordResult,
} from '../Enrich/people';
import { resolveIntake } from '../Enrich/intakePeople';
import { resolveCase, type CaseDetail, type CaseViewLike } from '../Enrich/casePeople';
import {
  resolveRegistration, type RegistrationDetail, type RegistrationViewLike, type RegistrationLinkLike,
} from '../Enrich/registrationPeople';
import {
  applyMember, createMemberCache, findMember, type MemberCache, type MemberDeps,
} from '../Enrich/memberLookup';
import {
  buildReview, buildRow, buildSummary, type EnrichSummary, type Review,
} from '../Enrich/output';
import { buildIncomplete, type IncompleteRow } from '../Enrich/incomplete';

export type EnrichStage = 'idle' | 'loading' | 'details' | 'review' | 'lookup' | 'done';
export interface EnrichProgress { label: string; done: number; total: number }

export type EnrichKind = 'request' | 'intake' | 'case' | 'registration';

const LIST_BATCH = 200;
const DETAIL_PARALLEL = 6;
const MEMBER_PARALLEL = 4;

interface Work {
  list: Row[];
  byId: Map<number, Row>;
  results: Map<number, RecordResult>;
  cache: MemberCache;
}
const newWork = (): Work => ({ list: [], byId: new Map(), results: new Map(), cache: createMemberCache() });

const textOrNull = (v: unknown) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v));

/**
 * The missing-data run for the Request and Intake exports: Source 1 (list) → Source 2 (detail per
 * record) → review → optional Source 3 (member lookup) → done. `kind` picks which detail call and
 * which person-resolving rules apply; everything else is shared.
 *
 * ⚠️ Cancel means start over. Every async step checks `token` after each await, so a cancelled run
 * can never write state; calls already in flight simply finish and are ignored.
 */
export function useExportEnrichment(kind: EnrichKind) {
  const requestService = useCareRequestService();
  const intakeService = useRegistrationIntakeService();
  const careCaseService = useCareCaseService();
  const registrationService = useRegistrationService();
  const memberService = useMemberService();

  /** The only places the two sources differ: the id column, the detail call and the person rules. */
  const adapter = useMemo(() => {
    type Ctx = { id: number; reference: string; listLocation: string | null };
    if (kind === 'registration') {
      return {
        idKey: 'registrationID',
        referenceKey: 'reference',
        // Source 2 for a registration: the junction rows (who is on it), the view (their details) and
        // the intake it came from (to tell the applicant from the people attending).
        fetch: async (id: number): Promise<RegistrationDetail> => {
          const [rawView, rawLinks] = await Promise.all([
            registrationService.getView(id), registrationService.getMembers(id),
          ]);
          const view = rawView as unknown as RegistrationViewLike;
          const links = (rawLinks as unknown as { data?: unknown })?.data ?? rawLinks;
          // ⚠️ Not caught: an intake that cannot be read marks the registration failed (and retried)
          // rather than guessing who is only the applicant.
          const intake = view?.intakeID != null ? await intakeService.getById(view.intakeID) : null;
          return {
            view,
            links: Array.isArray(links) ? (links as RegistrationLinkLike[]) : [],
            intake,
          };
        },
        resolve: (d: unknown, ctx: Ctx) => resolveRegistration(d as RegistrationDetail, ctx),
        requireLocation: false, // remote courses have no location
      };
    }
    if (kind === 'case') {
      return {
        idKey: 'caseID',
        referenceKey: 'name', // cases have no reference code; the name is the label
        // Source 2 for a case: its members, plus the request it came from (when it came from one).
        fetch: async (id: number): Promise<CaseDetail> => {
          const view = (await careCaseService.getView(id)) as unknown as CaseViewLike;
          const requestID = view?.requestID ?? null;
          // ⚠️ Not caught: if the case claims a request and it cannot be read, the case is marked
          // failed (and retried) instead of guessing who the requester is.
          const request = requestID != null ? await requestService.getById(requestID) : null;
          return { view, request };
        },
        resolve: (d: unknown, ctx: Ctx) => resolveCase(d as CaseDetail, ctx),
        requireLocation: true,
      };
    }
    if (kind === 'intake') {
      return {
        idKey: 'intakeID',
        referenceKey: 'reference',
        fetch: (id: number) => intakeService.getById(id),
        resolve: (d: unknown, ctx: Ctx) => resolveIntake(d as RegistrationIntakeModelView, ctx),
        requireLocation: false,
      };
    }
    return {
      idKey: 'requestID',
      referenceKey: 'reference',
      fetch: (id: number) => requestService.getById(id),
      resolve: (d: unknown, ctx: Ctx) => resolveRequest(d as CareRequestModelView, ctx),
      requireLocation: true,
    };
  }, [kind, requestService, intakeService, careCaseService, registrationService]);
  const idOf = useCallback((r: Row) => Number(r[adapter.idKey]), [adapter]);

  const [stage, setStage] = useState<EnrichStage>('idle');
  const [progress, setProgress] = useState<EnrichProgress | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [summary, setSummary] = useState<EnrichSummary | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [incomplete, setIncomplete] = useState<IncompleteRow[]>([]);

  const token = useRef(0);
  const work = useRef<Work>(newWork());

  // Leaving the page mid-run is the same as Cancel.
  useEffect(() => () => { token.current += 1; }, []);

  const ordered = useCallback(() =>
    work.current.list
      .map((r) => work.current.results.get(idOf(r)))
      .filter((r): r is RecordResult => Boolean(r)), [idOf]);

  const reset = useCallback(() => {
    token.current += 1;
    work.current = newWork();
    setStage('idle'); setProgress(null); setRows(null);
    setSummary(null); setReview(null); setRetrying(false);
    setIncomplete([]);
  }, []);

  /** Phase G: assemble the rows and finish. */
  const finalize = useCallback(() => {
    setRows(work.current.list.map((r) => buildRow(r, work.current.results.get(idOf(r)))));
    setSummary(buildSummary(ordered()));
    setIncomplete(buildIncomplete(ordered()));
    setReview(null); setProgress(null); setRetrying(false);
    setStage('done');
  }, [idOf, ordered]);

  /** Phase E: open the review modal, or skip straight to done when nothing is missing. */
  const settle = useCallback(() => {
    const rv = buildReview(ordered());
    if (rv.items.length === 0 && rv.failed.length === 0) { finalize(); return; }
    setReview(rv); setProgress(null); setRetrying(false);
    setStage('review');
  }, [finalize, ordered]);

  /** Phase D: one detail call per request id, limited parallelism. */
  const loadDetails = useCallback(async (ids: number[], tok: number, label: string) => {
    const stop = () => token.current !== tok;
    let done = 0;
    await runPool(ids, DETAIL_PARALLEL, async (id) => {
      const listRow = work.current.byId.get(id);
      const reference = String(listRow?.[adapter.referenceKey] ?? `#${id}`);
      const location = textOrNull(listRow?.locationName);
      try {
        const detail = await adapter.fetch(id);
        if (stop()) return;
        work.current.results.set(id, adapter.resolve(detail, { id, reference, listLocation: location }));
      } catch (e) {
        if (stop()) return;
        console.error(`[export] detail lookup failed for ${kind} ${id}:`, e); // a 403 lands here too
        work.current.results.set(id, failedResult(id, reference, location, adapter.requireLocation));
      } finally {
        done += 1;
        if (!stop()) setProgress({ label, done, total: ids.length });
      }
    }, stop);
  }, [adapter, kind]);

  const failedIds = () => ordered().filter((r) => r.failed).map((r) => r.id);

  /** Phase B–D: load every matching request, then fill the gaps from the detail call. */
  const start = useCallback(async (args: {
    fetchPage: (skip: number, take: number) => Promise<Row[]>;
    totalCount: number;
  } | {
    /** Streamed sources (cases): the list is already in memory. */
    rows: Row[];
  }) => {
    const tok = ++token.current;
    const stop = () => token.current !== tok;
    work.current = newWork();
    setRows(null); setSummary(null); setReview(null);
    setStage('loading');
    const add = (r: Row) => {
      const id = idOf(r);
      if (!work.current.byId.has(id)) { work.current.byId.set(id, r); work.current.list.push(r); }
    };

    try {
      if ('rows' in args) {
        args.rows.forEach(add);
      } else {
        setProgress({ label: 'Loading list', done: 0, total: args.totalCount });
        let skip = 0;
        while (skip < args.totalCount) {
          const page = await args.fetchPage(skip, Math.min(LIST_BATCH, args.totalCount - skip));
          if (stop()) return;
          if (page.length === 0) break; // server stopped early — don't spin
          page.forEach(add);
          skip += page.length;
          setProgress({ label: 'Loading list', done: Math.min(skip, args.totalCount), total: args.totalCount });
        }
      }

      setStage('details');
      const ids = work.current.list.map((r) => idOf(r));
      setProgress({ label: 'Reading request details', done: 0, total: ids.length });
      await loadDetails(ids, tok, 'Reading request details');
      if (stop()) return;

      const failed = failedIds(); // retry the failures once at the end
      if (failed.length) await loadDetails(failed, tok, 'Retrying failed requests');
      if (stop()) return;

      settle();
    } catch (e) {
      if (stop()) return;
      console.error('[export] enrichment failed:', e);
      toast.error('Could not fill details', 'Something went wrong while loading the requests. Please try again.');
      reset();
    }
  }, [loadDetails, settle, reset, idOf]);

  /** Review modal → Retry on the failed detail calls. */
  const retryFailed = useCallback(async () => {
    const tok = token.current;
    setRetrying(true);
    await loadDetails(failedIds(), tok, 'Retrying failed requests');
    if (token.current !== tok) return;
    settle();
  }, [loadDetails, settle]);

  /** Phase F: member lookup, only after the user accepted the warning. */
  const lookUpMembers = useCallback(async () => {
    const tok = token.current;
    const stop = () => token.current !== tok;
    const deps: MemberDeps = {
      getViewById: (id) => memberService.getViewById(id),
      searchMembers: (term, limit) => memberService.searchMembers(term, limit),
    };

    const targets: PersonResult[] = [];
    for (const r of work.current.results.values()) {
      if (r.failed) continue;
      [r.a, r.b].forEach((p) => { if (p && missingOf(p).length > 0) targets.push(p); });
    }

    setStage('lookup');
    setProgress({ label: 'Looking up members', done: 0, total: targets.length });
    let done = 0;
    await runPool(targets, MEMBER_PARALLEL, async (person) => {
      try {
        const member = await findMember(person, deps, work.current.cache);
        if (stop()) return;
        if (member) applyMember(person, member);
      } catch (e) {
        console.error('[export] member lookup failed:', e);
      } finally {
        done += 1;
        if (!stop()) setProgress({ label: 'Looking up members', done, total: targets.length });
      }
    }, stop);
    if (stop()) return;
    finalize();
  }, [memberService, finalize]);

  return {
    stage,
    progress,
    rows,
    summary,
    incomplete,
    review,
    retrying,
    /** Filters, search, sort and columns are locked from Start until Done. */
    locked: stage !== 'idle' && stage !== 'done',
    start,
    cancel: reset,
    reset,
    retryFailed,
    lookUpMembers,
    continueWithout: finalize,
  };
}
